import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ImageViewer } from '../components';
import { ADMIN_FEEDBACK_OWNER_ID } from '../threads/types';
import type { Thread, AppFeedbackKind } from '../threads/types';
import type { ThreadApi } from '../threads/threadApi';
import { getFeedbackKind } from './appFeedbackHelpers';
import { downscaleImageForUpload, DownscaleError } from './imageDownscale';
import './AppFeedbackForm.css';

const MAX_ATTACHMENTS = 3;

interface AppFeedbackFormProps {
    api: ThreadApi;
    /** Called after successful submit/edit. Receives the saved thread. */
    onSubmitted: (thread?: Thread) => void;
    onCancel: () => void;
    /** Show "On behalf of" email field (admin create mode only). */
    showOnBehalfOf?: boolean;
    /**
     * Edit mode — when set, prefills from this thread and calls
     * `api.update` on submit instead of `api.create`. The kind field
     * and on-behalf-of are hidden.
     */
    editing?: Thread;
}

interface Attachment {
    localId: string;
    status: 'uploading' | 'done' | 'error';
    /** Data / object URL used for thumbnail preview. */
    previewUrl: string;
    /** Final blob URL returned by the server (when status === 'done'). */
    url?: string;
    error?: string;
    /** True for items that already existed on the thread when we opened the edit form. */
    preExisting?: boolean;
}

function newLocalId(): string {
    return (globalThis.crypto?.randomUUID?.() ?? `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
}

function newThreadId(): string {
    return globalThis.crypto?.randomUUID?.() ?? v4Fallback();
}

function v4Fallback(): string {
    const b = new Uint8Array(16);
    for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'));
    return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`;
}

export function AppFeedbackForm({ api, onSubmitted, onCancel, showOnBehalfOf, editing }: AppFeedbackFormProps) {
    const isEdit = !!editing;

    // threadId is stable for the lifetime of the form — attachments upload
    // against this id before submit, so it must be fixed before any uploads
    // start. In edit mode we reuse the existing id.
    const [threadId] = useState<string>(() => editing?.id ?? newThreadId());

    const [kind, setKind] = useState<AppFeedbackKind>(() => (editing ? getFeedbackKind(editing) : undefined) ?? 'suggestion');
    const [title, setTitle] = useState(editing?.title ?? '');
    const [description, setDescription] = useState(editing?.description ?? '');
    const [onBehalfOfEmail, setOnBehalfOfEmail] = useState('');

    const [attachments, setAttachments] = useState<Attachment[]>(() =>
        (editing?.screenshotUrls ?? []).map((url) => ({
            localId: newLocalId(),
            status: 'done' as const,
            previewUrl: url,
            url,
            preExisting: true,
        }))
    );

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        return () => {
            for (const a of attachments) {
                if (!a.preExisting && a.previewUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(a.previewUrl);
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const uploadingCount = useMemo(
        () => attachments.filter((a) => a.status === 'uploading').length,
        [attachments],
    );
    const doneCount = useMemo(
        () => attachments.filter((a) => a.status === 'done').length,
        [attachments],
    );
    const slotsRemaining = Math.max(0, MAX_ATTACHMENTS - attachments.length);

    const handlePick = useCallback(async (files: FileList) => {
        const picked = Array.from(files).slice(0, slotsRemaining);
        if (picked.length === 0) return;

        const placeholders: Attachment[] = picked.map((f) => ({
            localId: newLocalId(),
            status: 'uploading',
            previewUrl: URL.createObjectURL(f),
        }));
        setAttachments((prev) => [...prev, ...placeholders]);

        for (let i = 0; i < picked.length; i++) {
            const file = picked[i];
            const placeholder = placeholders[i];
            try {
                const scaled = await downscaleImageForUpload(file);
                URL.revokeObjectURL(placeholder.previewUrl);
                const result = await api.uploadImage(threadId, scaled.base64, scaled.contentType);
                setAttachments((prev) => prev.map((a) =>
                    a.localId === placeholder.localId
                        ? { ...a, status: 'done', previewUrl: scaled.previewUrl, url: result.url }
                        : a,
                ));
            } catch (err) {
                const msg = err instanceof DownscaleError
                    ? err.message
                    : err instanceof Error ? err.message : 'Upload failed';
                setAttachments((prev) => prev.map((a) =>
                    a.localId === placeholder.localId
                        ? { ...a, status: 'error', error: msg }
                        : a,
                ));
            }
        }
    }, [api, threadId, slotsRemaining]);

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        if (slotsRemaining === 0) return;
        const items = e.clipboardData?.items;
        if (!items) return;
        const imageFiles: File[] = [];
        for (const item of items) {
            if (item.kind !== 'file') continue;
            if (!item.type.startsWith('image/')) continue;
            const f = item.getAsFile();
            if (f) imageFiles.push(f);
        }
        if (imageFiles.length === 0) return;
        e.preventDefault();
        const dt = new DataTransfer();
        for (const f of imageFiles) dt.items.add(f);
        handlePick(dt.files);
    }, [handlePick, slotsRemaining]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        if (slotsRemaining === 0) return;
        const files = e.dataTransfer?.files;
        if (files && files.length > 0) handlePick(files);
    }, [handlePick, slotsRemaining]);

    const handleRemove = useCallback((localId: string) => {
        setAttachments((prev) => {
            const victim = prev.find((a) => a.localId === localId);
            if (victim && !victim.preExisting && victim.previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(victim.previewUrl);
            }
            return prev.filter((a) => a.localId !== localId);
        });
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim() || !description.trim()) return;
        if (uploadingCount > 0) return;

        const screenshotUrls = attachments
            .filter((a) => a.status === 'done' && a.url)
            .map((a) => a.url!);

        setSubmitting(true);
        setError('');
        try {
            if (isEdit) {
                const updated = await api.update(threadId, ADMIN_FEEDBACK_OWNER_ID, {
                    title: title.trim(),
                    description: description.trim(),
                    screenshotUrls,
                });
                onSubmitted(updated);
            } else {
                const created = await api.create({
                    id: threadId,
                    type: 'app_feedback',
                    ownerId: ADMIN_FEEDBACK_OWNER_ID,
                    feedbackKind: kind,
                    title: title.trim(),
                    description: description.trim(),
                    ...(screenshotUrls.length > 0 ? { screenshotUrls } : {}),
                    appContext: {
                        url: window.location.href,
                        userAgent: navigator.userAgent,
                    },
                    ...(showOnBehalfOf && onBehalfOfEmail.trim() ? { onBehalfOfEmail: onBehalfOfEmail.trim() } : {}),
                });
                onSubmitted(created);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : (isEdit ? 'Failed to save' : 'Failed to submit'));
        } finally {
            setSubmitting(false);
        }
    };

    const canSubmit = !submitting
        && uploadingCount === 0
        && !!title.trim()
        && !!description.trim();

    return (
        <form className="feedback-form" onSubmit={handleSubmit} onPaste={handlePaste}>
            {!isEdit && (
                <div className={showOnBehalfOf ? 'feedback-form-row' : ''}>
                    <div className="feedback-form-field">
                        <label htmlFor="feedback-type">Issue Type</label>
                        <select
                            id="feedback-type"
                            value={kind}
                            onChange={(e) => setKind(e.target.value as AppFeedbackKind)}
                        >
                            <option value="bug">Bug Report</option>
                            <option value="suggestion">Suggestion</option>
                        </select>
                    </div>

                    {showOnBehalfOf && (
                        <div className="feedback-form-field">
                            <label htmlFor="feedback-behalf">On behalf of (optional)</label>
                            <input
                                id="feedback-behalf"
                                type="email"
                                value={onBehalfOfEmail}
                                onChange={(e) => setOnBehalfOfEmail(e.target.value)}
                                placeholder="student@email.com — leave empty for yourself"
                            />
                        </div>
                    )}
                </div>
            )}

            <div className="feedback-form-field">
                <label htmlFor="feedback-title">Title</label>
                <input
                    id="feedback-title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={kind === 'bug' ? 'Brief description of the issue' : 'Your idea in a few words'}
                    maxLength={200}
                    required
                />
            </div>

            <div className="feedback-form-field">
                <label htmlFor="feedback-desc">Description</label>
                <textarea
                    id="feedback-desc"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={kind === 'bug' ? 'What happened? What did you expect?' : 'Describe your suggestion in detail'}
                    rows={5}
                    maxLength={5000}
                    required
                />
            </div>

            <div className="feedback-form-field">
                <label>Screenshots (optional)</label>
                <div
                    className="feedback-attachments"
                    onDrop={handleDrop}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                >
                    {attachments.map((a) => (
                        <div
                            key={a.localId}
                            className={`feedback-attachment feedback-attachment--${a.status}`}
                            title={a.error || (a.status === 'uploading' ? 'Uploading…' : undefined)}
                        >
                            <ImageViewer src={a.previewUrl} alt="" className="feedback-attachment-thumb" />
                            {a.status === 'uploading' && (
                                <div className="feedback-attachment-overlay">
                                    <span className="feedback-attachment-spinner" aria-hidden />
                                </div>
                            )}
                            {a.status === 'error' && (
                                <div className="feedback-attachment-overlay feedback-attachment-overlay--error">!</div>
                            )}
                            <button
                                type="button"
                                className="feedback-attachment-remove"
                                onClick={() => handleRemove(a.localId)}
                                aria-label="Remove image"
                            >
                                ×
                            </button>
                        </div>
                    ))}

                    {slotsRemaining > 0 && (
                        <div
                            role="button"
                            tabIndex={0}
                            aria-label="Add photo — paste, drop, or click"
                            className={`feedback-attachment-add${dragOver ? ' feedback-attachment-add--dragover' : ''}`}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    fileInputRef.current?.click();
                                }
                            }}
                            onPaste={handlePaste}
                            onDrop={handleDrop}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                        >
                            <span aria-hidden>+</span>
                            <span className="feedback-attachment-add-label">Paste, drop</span>
                            <span className="feedback-attachment-add-label">or click</span>
                        </div>
                    )}
                </div>
                <p className="feedback-attachment-hint">
                    {doneCount}/{MAX_ATTACHMENTS} added
                    {MAX_ATTACHMENTS - doneCount > 0 && ` — up to ${MAX_ATTACHMENTS} images, 1 MB each. You can paste (⌘/Ctrl+V) or drag a screenshot straight in. Large images are downscaled automatically.`}
                </p>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                            handlePick(e.target.files);
                        }
                        e.target.value = '';
                    }}
                />
            </div>

            {error && <p className="feedback-form-error">{error}</p>}

            <div className="feedback-form-actions">
                <Button type="button" onClick={onCancel}>Cancel</Button>
                <Button type="submit" disabled={!canSubmit}>
                    {submitting
                        ? (isEdit ? 'Saving…' : 'Submitting…')
                        : uploadingCount > 0
                            ? `Uploading ${uploadingCount}…`
                            : isEdit ? 'Save' : 'Submit'}
                </Button>
            </div>
        </form>
    );
}
